import { forwardRef, memo, useCallback, type ReactNode } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import { makeStyles } from 'tss-react/mui';
import { type Theme } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
// Skillmore UI Components
import { type Values } from '@empathco/ui-components/src/helpers/intl';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    color: theme.palette.secondary.text,
    fontWeight: theme.typography.subtitle1.fontWeight,
    borderRadius: theme.shape.borderRadius
  }
}));

type CategoryMenuItemProps = {
  readonly id: number;
  readonly title?: string;
  readonly values?: Values;
  readonly icon?: ReactNode;
  readonly selected: boolean;
  readonly onClick: (value: number) => void;
};

const CategoryMenuItem = forwardRef<HTMLLIElement, CategoryMenuItemProps>(({
  id,
  title,
  values,
  icon,
  selected,
  onClick
}, ref) => {
  const { formatMessage } = useIntl();
  const { classes } = useStyles();
  const handleClick = useCallback(() => onClick(id), [id, onClick]);
  return (
    <MenuItem
        ref={ref}
        // data-value={id}
        // aria-selected={selected} // TODO: does not work, so using hack with `aria-label` and 'browser.selected'
        aria-label={formatMessage({ id: 'browser.selected' }, { title: title || null, selected})}
        aria-live="assertive"
        selected={selected}
        onClick={handleClick}
        classes={classes}
    >
      <FormattedMessage id={title || 'browser.all'} values={values} defaultMessage={title}/>
      {icon ? (
        <>
          {'\u00A0'}
          {icon}
        </>
      ) : undefined}
    </MenuItem>
  );
});

CategoryMenuItem.displayName = 'CategoryMenuItem';

export default memo(CategoryMenuItem);
